import React, { Suspense, lazy } from "react";
import { ThemeProvider } from "styled-components";
import { type PlatformGraphQL, BullrunProviders, PlatformGraphQLProvider } from "@orb-labs/orby-react";
import { GlobalStyles } from "@/GlobalStyles";
import { theme } from "@/theme";
import { useScreenView } from "@/analytics/useScreenView";
import { usePromoOutreachRedirect } from "@/hooks/usePromoOutreachRedirect";
import { AuthenticationProvider } from "@/providers/AuthenticationProvider";
import { LoginActionsProvider } from "@/providers/LoginActionsProvider";
import { WebDeviceSecurityProvider } from "@/providers/WebDeviceSecurityProvider";
import { WebAttributionProvider } from "@/providers/WebAttributionProvider";
import { getWebAnalyticsBackend, getWebErrorBackend } from "@/observability";
import { webPlatformObservability } from "@/observability/PlatformObservability";
import { webPlatformLifecycle } from "@/lifecycle/PlatformLifecycle";
import { ENV } from "@/config/env";
import { useIsMobileViewport } from "@/hooks/useIsMobileViewport";

const DesktopRoutes = lazy(() => import("@/components/DesktopRoutes"));
// Mobile web now renders the shared React Native screens from
// @orb-labs/bullrun-mobile-screens via react-native-web.
const MobileRouter = lazy(() => import("@/mobile-shared-entry"));

// PlatformGraphQLProvider is typed against @types/react@18.
const GraphQLContextProvider = PlatformGraphQLProvider as unknown as React.FC<{
  value: PlatformGraphQL;
  children: React.ReactNode;
}>;

const webGraphQL: PlatformGraphQL = {
  baseUrlPrefix: `${ENV.ORBY_GRAPHQL_BASE_URL}/${ENV.ORBY_INSTANCE_PRIVATE_API_KEY}`,
  observability: webPlatformObservability,
};

function AppInner() {
  useScreenView();
  usePromoOutreachRedirect();
  const isMobile = useIsMobileViewport();

  return (
    <BullrunProviders
      appName={ENV.ORBY_APP_NAME}
      instancePrivateAPIKey={ENV.ORBY_INSTANCE_PRIVATE_API_KEY}
      instancePublicAPIKey={ENV.ORBY_INSTANCE_PUBLIC_API_KEY}
      baseUrl={ENV.ORBY_RPC_BASE_URL}
      observability={webPlatformObservability}
      lifecycle={webPlatformLifecycle}
      analytics={getWebAnalyticsBackend()}
      errorMonitoring={getWebErrorBackend()}
      loadingFallback={undefined}
    >
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <Suspense fallback={null}>{isMobile ? <MobileRouter /> : <DesktopRoutes />}</Suspense>
      </ThemeProvider>
    </BullrunProviders>
  );
}

export default function App() {
  return (
    <GraphQLContextProvider value={webGraphQL}>
      <WebDeviceSecurityProvider>
        <WebAttributionProvider>
          <AuthenticationProvider>
            <LoginActionsProvider>
              <AppInner />
            </LoginActionsProvider>
          </AuthenticationProvider>
        </WebAttributionProvider>
      </WebDeviceSecurityProvider>
    </GraphQLContextProvider>
  );
}
